import React, { useState, useCallback } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { FaCamera, FaCheck, FaTimes, FaSearch } from 'react-icons/fa';
import { X } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { applyClientMembershipToReceipt, updatePaymentReceiptSuggestedTip } from '../../../../../../../../../../../crud/payments_api/payment_receipts';
import QRScanner from '../../../../../../../../../../../components/common/QRCodes/QRScanner';
import MiniLoadingSpinner from '../../../../../../../../../../../components/Spinners/MiniLoadingSpinner';
import SearchClientModal from './components/SearchClientModal/SearchClientModal';
import './MembershipSection.scss';

interface MembershipSectionProps {
  receiptId: number;
  receipts: any[];
  setReceipts: (receipts: any[]) => void;
  onMembershipApplied?: () => void;
}

const MembershipSection: React.FC<MembershipSectionProps> = ({
  receiptId,
  receipts,
  setReceipts,
  onMembershipApplied,
}) => {
  const [clientCode, setClientCode] = useState('');
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [qrInput, setQRInput] = useState(false);
  const [loading, setLoading] = useState(false);

  // Extract client ID from code format: ABCD-123 -> 123
  const extractClientId = (input: string): number | null => {
    // Remove whitespace
    const cleanInput = input.trim();
    
    // Check if it contains a dash
    if (cleanInput.includes('-')) {
      const parts = cleanInput.split('-');
      const idPart = parts[parts.length - 1]; // Get the part after the last dash
      const clientId = parseInt(idPart, 10);
      return isNaN(clientId) ? null : clientId;
    }
    
    // If no dash, try to parse as number directly
    const clientId = parseInt(cleanInput, 10);
    return isNaN(clientId) ? null : clientId;
  };

  // Handle QR code scanned
  const handleQRCodeScanned = (scannedContent: string) => {
    setClientCode(scannedContent);
  };

  // Apply membership to receipt
  const applyMembership = useCallback(async (clientId: number) => {
    if (loading) return;

    setLoading(true);

    try {
      const response = await applyClientMembershipToReceipt(receiptId, clientId);
      
      if (response.status === 200 && response.data) {
        const { receipt: updatedReceipt, membership } = response.data;

        // Get the current receipt to calculate tip adjustment
        const currentReceipt = receipts.find((r: any) => r.receipt_id === receiptId);
        
        // Recalculate tip proportionally after membership discount
        let newSuggestedTip = updatedReceipt.suggested_tip || 0;
        if (currentReceipt && currentReceipt.suggested_tip > 0) {
          // Calculate the tip rate before the membership discount
          const oldNetBase = currentReceipt.subtotal - (currentReceipt.coupon_amount || 0) - (currentReceipt.discount_amount || 0);
          const tipRate = oldNetBase > 0 ? currentReceipt.suggested_tip / oldNetBase : 0;
          
          // Calculate new net base after the membership discount
          const newNetBase = updatedReceipt.subtotal - (updatedReceipt.coupon_amount || 0) - (updatedReceipt.discount_amount || 0);
          
          // Calculate new tip based on the same rate
          newSuggestedTip = Math.round(newNetBase * tipRate);
          
          // Update tip in backend
          try {
            await updatePaymentReceiptSuggestedTip(receiptId, newSuggestedTip);
          } catch (error) {
            console.error('Error updating tip after membership:', error);
            // Don't fail the whole operation if tip update fails
          }
        }

        // Update receipts array with the updated receipt
        const updatedReceipts = receipts.map((r: any) => {
          if (r.receipt_id === receiptId) {
            return {
              ...r,
              ...updatedReceipt,
              client_name: updatedReceipt.client_name,
              client_id: clientId,
              membership_info: membership,
              discount_amount: updatedReceipt.discount_amount,
              manual_discount_amount: updatedReceipt.manual_discount_amount,
              suggested_tip: newSuggestedTip,
            };
          }
          return r;
        });
        
        setReceipts(updatedReceipts);

        // Show success message with membership details
        toast.success(
          `Membresía aplicada: ${membership.membership_name} (${membership.discount_percentage}% descuento)`
        );

        // Close the panel and reset
        setClientCode('');
        setQRInput(false);

        // Callback if provided
        if (onMembershipApplied) {
          onMembershipApplied();
        }
      }
    } catch (error: any) {
      console.error('Error applying membership:', error);
      
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error?.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Error al aplicar la membresía');
      }
    } finally {
      setLoading(false);
    }
  }, [receiptId, receipts, setReceipts, loading, onMembershipApplied]);

  // Handle manual code apply
  const handleCodeApply = () => {
    const clientId = extractClientId(clientCode);
    
    if (!clientId) {
      toast.error('Código de cliente inválido');
      return;
    }

    applyMembership(clientId);
  };

  // Handle client selected from search modal
  const handleClientSelected = (clientId: number) => {
    setShowSearchModal(false);
    applyMembership(clientId);
  };

  // Auto-apply when QR is scanned
  React.useEffect(() => {
    if (qrInput && clientCode) {
      const clientId = extractClientId(clientCode);
      if (clientId) {
        applyMembership(clientId);
      }
    }
  }, [qrInput, clientCode, applyMembership]);

  return (
    <>
      <div className="membership-validation">
        <div className="membership-input-row">
          <Form.Control
            type="text"
            value={clientCode}
            placeholder="Código de cliente (ej: DIET-123)"
            className="membership-input"
            onChange={(e) => setClientCode(e.target.value)}
            disabled={loading}
          />
          <Button
            variant="none"
            className="qr-scan-button"
            onClick={() => setShowQRScanner(true)}
            disabled={loading}
            title="Escanear código QR"
          >
            <FaCamera />
          </Button>
        </div>

        <div className="membership-actions">
          <Button
            variant="none"
            onClick={() => setShowSearchModal(true)}
            className="action-button search-button"
            disabled={loading}
          >
            <FaSearch />
            <span>Buscar Cliente</span>
          </Button>
          <Button
            variant="none"
            onClick={() => {
              setClientCode('');
              setQRInput(false);
              if (onMembershipApplied) onMembershipApplied();
            }}
            className="action-button secondary"
            disabled={loading}
          >
            <FaTimes />
            <span>Cancelar</span>
          </Button>
          <Button
            variant="none"
            onClick={handleCodeApply}
            className="action-button primary"
            disabled={loading || !clientCode.trim()}
          >
            <FaCheck />
            <span>Aplicar</span>
          </Button>
        </div>
      </div>

      {/* QR Scanner Modal */}
      <Modal
        show={showQRScanner}
        onHide={() => setShowQRScanner(false)}
        centered
        size="lg"
      >
        <Modal.Header>
          <div className="form-modal-title-container">
            <Modal.Title className="form-modal-title">
              Escanear código de cliente
            </Modal.Title>
          </div>
          <Button
            variant="none"
            className="modal-close-button"
            onClick={() => setShowQRScanner(false)}
          >
            <X size={24} />
          </Button>
        </Modal.Header>
        <Modal.Body>
          <QRScanner
            isOpen={showQRScanner}
            setIsOpen={setShowQRScanner}
            setQRContent={handleQRCodeScanned}
            setQrInput={setQRInput}
          />
        </Modal.Body>
      </Modal>

      {/* Search Client Modal */}
      <SearchClientModal
        show={showSearchModal}
        onHide={() => setShowSearchModal(false)}
        onClientSelect={handleClientSelected}
      />

      {loading && <MiniLoadingSpinner left="50%" />}
    </>
  );
};

export default MembershipSection;